import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Database, Copy, Check, ExternalLink, Code2, FlaskConical } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import JsonLd from "../components/JsonLd";
import CiteThisPage from "../components/CiteThisPage";
import { usePageSEO } from "../hooks/usePageSEO";

const BASE = "https://www.peptidesformedspas.org";

/**
 * /glossary — public glossary index (mirrors backend /api/glossary/).
 *
 * Purpose: a citation-ready, human-readable hub that lists every compound
 * we cover with a link to (a) the marketing/product page, (b) the
 * structured JSON fact sheet AI crawlers and journalists can quote. The
 * "free JSON API" call-out is the linkable-asset hook: developer blogs,
 * AI-tool builders, and directory sites link to free structured data.
 */

const CURL_EXAMPLE = `curl -s https://www.peptidesformedspas.org/api/glossary/bpc-157`;
const FETCH_EXAMPLE = `fetch("https://www.peptidesformedspas.org/api/glossary/bpc-157")
  .then(r => r.json())
  .then(entry => console.log(entry.name, entry.regulatory_status));`;

const GlossaryIndexPage = () => {
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);
  usePageSEO();

  useEffect(() => {
    let active = true;
    const api = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${api}/api/glossary/`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        if (!active) return;
        setEntries(data.terms || []);
      })
      .catch((e) => {
        if (!active) return;
        setError(e.message);
      });
    return () => { active = false; };
  }, []);

  const copyText = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1600);
    } catch (_) {
      /* clipboard unavailable — no user notification needed */
    }
  };

  const pageUrl = `${BASE}/glossary`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "DefinedTermSet",
      "@id": `${pageUrl}#terms`,
      name: "PeptidesForMedSpas Peptide Glossary",
      url: pageUrl,
      description:
        "Structured, citation-ready peptide glossary. Each entry available as human-readable page and free structured JSON via /api/glossary/{slug}.",
      inLanguage: "en-US",
      publisher: { "@id": `${BASE}/#organization` },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${pageUrl}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
        { "@type": "ListItem", position: 2, name: "Glossary", item: pageUrl },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#fffaf5]" data-testid="glossary-index-page">
      <Header />
      <JsonLd id="glossary-index-jsonld" data={jsonLd} />

      {/* Hero */}
      <section className="pt-32 pb-10 bg-gradient-to-b from-[#fdf2f4] to-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center text-sm text-[#a09186] mb-6">
            <Link to="/" className="hover:text-[#b89a6d]">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-[#3a2a26] font-medium">Glossary</span>
          </nav>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#fdf2f4] border border-[#e8c5b8] rounded-full mb-4">
            <Database className="w-4 h-4 text-[#b89a6d]" />
            <span className="text-sm text-[#b89a6d] font-medium">Free Structured JSON API</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#3a2a26] mb-6" data-testid="glossary-index-h1">
            Peptide Glossary
          </h1>
          <p className="text-base md:text-lg text-[#5a4a40] max-w-3xl leading-relaxed">
            Every compound in our catalog, with a plain-English name, aliases, category, evidence tier, mechanism summary, and regulatory status. Each entry is also available as a free structured JSON fact sheet at <code className="bg-white border border-[#f0d8d4] rounded px-1.5 py-0.5 text-xs">/api/glossary/&#123;slug&#125;</code> — designed to be quoted by AI engines, cited in research pieces, and embedded on other sites.
          </p>
        </div>
      </section>

      {/* Terms grid */}
      <section className="py-10 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl md:text-2xl font-bold text-[#3a2a26]">
              {entries.length > 0 ? `${entries.length} compounds` : "Loading compounds…"}
            </h2>
            <Link
              to="/peptide-glossary"
              data-testid="glossary-index-to-pillar"
              className="text-sm text-[#b89a6d] font-semibold hover:text-[#a08560] inline-flex items-center gap-1"
            >
              Deep-dive pillar page <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {error && (
            <div className="bg-rose-50 border border-rose-300 rounded-lg p-4 text-sm text-rose-700 mb-4" data-testid="glossary-index-error">
              Live glossary API unreachable ({error}). The static glossary catalog is available at{" "}
              <Link to="/peptide-glossary" className="underline">/peptide-glossary</Link>.
            </div>
          )}

          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="glossary-index-list">
            {entries.map((t) => (
              <li key={t.slug}>
                <article className="bg-[#fffaf5] border border-[#f0d8d4] rounded-xl p-5 h-full flex flex-col">
                  <div className="flex items-start gap-2 mb-2">
                    <FlaskConical className="w-4 h-4 text-[#b89a6d] flex-shrink-0 mt-0.5" />
                    <h3 className="text-sm md:text-base font-bold text-[#3a2a26] leading-snug">{t.name}</h3>
                  </div>
                  <p className="text-xs text-[#7a6b62] mb-3 flex-1">
                    Slug: <code className="bg-white border border-[#f0d8d4] rounded px-1 py-0.5">{t.slug}</code>
                  </p>
                  <div className="flex flex-wrap gap-2 mt-auto">
                    <Link
                      to={`/peptides/${t.slug}`}
                      data-testid={`glossary-index-link-${t.slug}`}
                      className="text-xs px-3 py-1.5 bg-[#d4a5ad] hover:bg-[#c4949c] text-white font-semibold rounded-full transition-colors"
                    >
                      Product page
                    </Link>
                    <a
                      href={`/api/glossary/${t.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`glossary-index-json-${t.slug}`}
                      className="text-xs px-3 py-1.5 bg-white border border-[#d4a5ad] text-[#b89a6d] font-semibold rounded-full hover:bg-[#fdf2f4] transition-colors inline-flex items-center gap-1"
                    >
                      JSON <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </article>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Developer / AI usage */}
      <section className="py-10 bg-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26] mb-4 flex items-center gap-2">
            <Code2 className="w-6 h-6 text-[#b89a6d]" /> Free to use, free to cite
          </h2>
          <p className="text-sm md:text-base text-[#5a4a40] mb-5 max-w-3xl">
            The <code className="bg-white border border-[#f0d8d4] rounded px-1.5 py-0.5 text-xs">/api/glossary/&#123;slug&#125;</code> endpoint returns a self-contained fact sheet with name, aliases, category, tier, chemical composition, mechanism summary, and regulatory status. Cached for 24 hours at the CDN. No API key required.
          </p>

          <div className="space-y-4">
            <article className="bg-white border border-[#f0d8d4] rounded-xl p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold text-[#3a2a26]">curl</h3>
                <button
                  type="button"
                  onClick={() => copyText(CURL_EXAMPLE, "curl")}
                  data-testid="glossary-index-copy-curl"
                  className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#fdf2f4] hover:bg-[#f0d8d4] text-[#b89a6d] text-xs font-semibold rounded-full transition-colors"
                >
                  {copied === "curl" ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied === "curl" ? "Copied" : "Copy"}
                </button>
              </div>
              <pre className="text-xs text-[#5a4a40] bg-[#fffaf5] border border-[#f0d8d4] rounded-lg p-3 whitespace-pre-wrap break-words font-mono leading-relaxed">
                {CURL_EXAMPLE}
              </pre>
            </article>

            <article className="bg-white border border-[#f0d8d4] rounded-xl p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold text-[#3a2a26]">JavaScript (fetch)</h3>
                <button
                  type="button"
                  onClick={() => copyText(FETCH_EXAMPLE, "fetch")}
                  data-testid="glossary-index-copy-fetch"
                  className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#fdf2f4] hover:bg-[#f0d8d4] text-[#b89a6d] text-xs font-semibold rounded-full transition-colors"
                >
                  {copied === "fetch" ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied === "fetch" ? "Copied" : "Copy"}
                </button>
              </div>
              <pre className="text-xs text-[#5a4a40] bg-[#fffaf5] border border-[#f0d8d4] rounded-lg p-3 whitespace-pre-wrap break-words font-mono leading-relaxed">
                {FETCH_EXAMPLE}
              </pre>
            </article>
          </div>

          <p className="text-xs text-[#7a6b62] italic mt-4">
            Attribution requested but not required: <em>PeptidesForMedSpas — https://www.peptidesformedspas.org</em>. Educational research content only; not medical advice.
          </p>
        </div>
      </section>

      {/* Cite */}
      <section className="py-10 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <CiteThisPage
            title="Peptide Glossary — PeptidesForMedSpas"
            url={pageUrl}
            publishedDate="2026-05-26"
          />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GlossaryIndexPage;
